import { useState } from "react";
import { motion } from "framer-motion";
import {
    Rocket,
    CheckCircle2,
    Users2,
    Gift,
    Calendar,
    ArrowUpRight,
    Sparkles,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
    HoneypotField,
    useFormSecurity,
    withFormSecurity,
} from "../lib/formSecurity";

import { SAAS_API_URL } from "../lib/env";

const SAAS_API = SAAS_API_URL;

const PERKS = [
    { icon: Gift, t: "Every module unlocked", d: "Sales, Operations, HR, Projects, Communication, Intelligence — no feature gating." },
    { icon: Sparkles, t: "AI Co‑pilot included", d: "Full access to the cross-module reasoning layer from day one." },
    { icon: Calendar, t: "Up to 8 seats, forever", d: "Stay free as long as your team is 8 or fewer. Grow when you want." },
    { icon: Users2, t: "Founder office hours", d: "Monthly group call with the ApexBrain team to unblock your setup." },
];

const ELIGIBILITY = [
    "Your team size is 8 people or fewer (including founders).",
    "You are a registered company, LLP, or pre-incorporation team.",
    "You are not a subsidiary of a company with >50 employees.",
    "You agree to share an anonymous quarterly outcomes summary.",
];

const FAQ = [
    {
        q: "What happens when we grow past 8 seats?",
        a: "Nothing breaks. You can keep using your data, and we’ll show a one-click upgrade to a discounted Growth plan with grandfathered pricing for early adopters.",
    },
    {
        q: "Is this a trial or actually free?",
        a: "Actually free. No credit card, no expiry. As long as your team is under 8, you can run your entire company on ApexBrain at zero cost.",
    },
    {
        q: "Do we get support?",
        a: "Yes — community support plus access to monthly founder office hours. Priority support is included on paid plans.",
    },
    {
        q: "What about our data if we leave?",
        a: "It’s yours. Export everything as JSON or CSV at any time. We’ll never hold your data hostage.",
    },
];

export default function StartupsPage() {
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        website_url: "",
        linkedin: "",
        contact: "",
        size: "1-3",
        about: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const { security, setSecurity, resetSecurity } = useFormSecurity();

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.company) {
            toast.error("Please fill name, work email and company.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("That email doesn’t look right.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${SAAS_API}/api/public/v1/contact-inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(withFormSecurity({
                    source: "startups",
                    name: form.name,
                    email: form.email,
                    company: form.company,
                    website_url: form.website_url.trim() || null,
                    linkedin: form.linkedin.trim() || null,
                    contact: form.contact.trim() || null,
                    size: form.size,
                    about: form.about,
                }, security)),
            });
            const json = await res.json();
            if (!res.ok || !json.success) {
                throw new Error(json.message || "Submission failed");
            }
            toast.success(
                "Application received. We’ll be in touch within 1 business day.",
            );
            setForm({
                name: "",
                company: "",
                email: "",
                website_url: "",
                linkedin: "",
                contact: "",
                size: "1-3",
                about: "",
            });
            resetSecurity();
        } catch (err) {
            toast.error(err.message || "Could not submit your application. Try again later.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-28 lg:pt-32" data-testid="startups-page">
            <Toaster position="top-right" theme="dark" richColors />
            {/* Hero */}
            <section className="relative overflow-hidden py-16 lg:py-24">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1 mb-6">
                            <Rocket className="w-3.5 h-3.5 text-emerald-300" />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-300">
                                ApexBrain for Startups
                            </span>
                        </div>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="startups-title"
                        >
                            Every successful business is built twice -
                            <br />
                            <span className="gradient-text">
                                first by people, then by systems.
                            </span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed font-light">
                            We&apos;re making sure founders can build both from
                            day one.
                        </p>
                        <p className="mt-4 max-w-xl text-base text-zinc-400 leading-relaxed font-light">
                            ApexBrain provides the operational foundation for
                            growth, helping startups create alignment,
                            accountability, and execution before complexity
                            becomes a bottleneck.
                        </p>
                        <p className="mt-6 text-sm sm:text-base font-semibold text-emerald-300">
                            Build the company first. Pay when the company grows.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-6 relative overflow-hidden">
                            <div className="absolute -top-20 -right-20 w-56 h-56 bg-electric/20 blur-3xl rounded-full pointer-events-none" />
                            <p className="font-code text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-3">
                                // what it costs
                            </p>
                            <div className="font-outfit text-5xl font-semibold text-white tabular-nums">
                                $0
                                <span className="text-zinc-500 text-lg font-light ml-2">
                                    / month, forever
                                </span>
                            </div>
                            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                {[
                                    "Up to 8 seats",
                                    "Every module",
                                    "AI Co-pilot",
                                    "All integrations",
                                ].map((x) => (
                                    <div
                                        key={x}
                                        className="flex items-center gap-2 text-zinc-200"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                                        <span>{x}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Perks */}
            <section className="relative py-12">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PERKS.map((p, i) => (
                        <motion.div
                            key={p.t}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.06 }}
                            className="rounded-2xl border border-white/[0.06] bg-obsidian-200/70 p-5"
                            data-testid={`startups-perk-${i}`}
                        >
                            <p.icon className="w-4 h-4 text-electric mb-3" />
                            <div className="font-outfit text-sm font-semibold text-white">
                                {p.t}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                                {p.d}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Eligibility + Form */}
            <section className="relative py-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-3">
                            // eligibility
                        </p>
                        <h2 className="font-outfit text-2xl sm:text-3xl font-semibold text-white leading-snug">
                            The fine print, made honest.
                        </h2>
                        <ul className="mt-6 space-y-3">
                            {ELIGIBILITY.map((e, i) => (
                                <li
                                    key={e}
                                    className="flex items-start gap-3 text-sm text-zinc-300"
                                    data-testid={`startups-eligibility-${i}`}
                                >
                                    <CheckCircle2 className="shrink-0 w-4 h-4 mt-0.5 text-emerald-300" />
                                    <span>{e}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <form
                        onSubmit={submit}
                        className="lg:col-span-7 rounded-2xl border border-white/[0.08] bg-obsidian-200/70 p-6 sm:p-8 space-y-5 relative"
                        data-testid="startups-form"
                    >
                        <HoneypotField
                            value={security._hp}
                            onChange={(v) => setSecurity((s) => ({ ...s, _hp: v }))}
                        />
                        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-electric">
                            // apply · 60 seconds
                        </p>
                        <h3 className="font-outfit text-xl font-semibold text-white">
                            Tell us who you are.
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field
                                label="Your name"
                                testId="startups-form-name"
                                value={form.name}
                                onChange={(v) => setForm({ ...form, name: v })}
                                placeholder="Aanya Mehta"
                            />
                            <Field
                                label="Work email"
                                testId="startups-form-email"
                                value={form.email}
                                onChange={(v) => setForm({ ...form, email: v })}
                                placeholder="aanya@yourstartup.com"
                                type="email"
                            />
                            <Field
                                label="Company"
                                testId="startups-form-company"
                                value={form.company}
                                onChange={(v) =>
                                    setForm({ ...form, company: v })
                                }
                                placeholder="Ledgerly"
                            />
                            <Field
                                label="Website"
                                testId="startups-form-website"
                                value={form.website_url}
                                onChange={(v) =>
                                    setForm({ ...form, website_url: v })
                                }
                                placeholder="https://yourstartup.com"
                            />
                            <Field
                                label="LinkedIn"
                                testId="startups-form-linkedin"
                                value={form.linkedin}
                                onChange={(v) =>
                                    setForm({ ...form, linkedin: v })
                                }
                                placeholder="linkedin.com/in/you or company page"
                            />
                            <Field
                                label="Contact number"
                                testId="startups-form-contact"
                                type="tel"
                                value={form.contact}
                                onChange={(v) =>
                                    setForm({ ...form, contact: v })
                                }
                                placeholder="+91 98765 43210"
                            />
                            <div>
                                <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
                                    Team size
                                </label>
                                <select
                                    data-testid="startups-form-size"
                                    value={form.size}
                                    onChange={(e) =>
                                        setForm({ ...form, size: e.target.value })
                                    }
                                    className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 font-code"
                                >
                                    {["1-3", "4-6", "7-8", "more than 8"].map(
                                        (s) => (
                                            <option
                                                key={s}
                                                value={s}
                                                className="bg-obsidian-300"
                                            >
                                                {s}
                                            </option>
                                        ),
                                    )}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
                                What are you building?
                            </label>
                            <textarea
                                data-testid="startups-form-about"
                                value={form.about}
                                onChange={(e) =>
                                    setForm({ ...form, about: e.target.value })
                                }
                                rows={3}
                                placeholder="One line about your startup…"
                                className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-6 py-3 rounded-full hover:brightness-110 transition disabled:opacity-60"
                            data-testid="startups-form-submit"
                        >
                            Apply for free access
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                        </button>
                        <p className="text-[11px] text-zinc-600 font-code">
                            We review applications manually within one business
                            day. No bots.
                        </p>
                    </form>
                </div>
            </section>

            {/* FAQ */}
            <section className="relative py-16 pb-28">
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    <h2 className="font-outfit text-2xl sm:text-3xl font-semibold text-white leading-snug mb-8">
                        Quick questions.
                    </h2>
                    <div className="space-y-3">
                        {FAQ.map((f, i) => (
                            <details
                                key={f.q}
                                className="group rounded-xl border border-white/[0.08] bg-obsidian-200/70 px-5 py-4 open:bg-white/[0.03] transition"
                                data-testid={`startups-faq-${i}`}
                            >
                                <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-semibold text-white">
                                    {f.q}
                                    <span className="font-code text-xs text-zinc-500 group-open:rotate-180 transition-transform">
                                        ▾
                                    </span>
                                </summary>
                                <p className="mt-3 text-sm text-zinc-400 leading-relaxed font-light">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

const Field = ({ label, value, onChange, placeholder, type = "text", testId }) => (
    <div>
        <label className="block text-[11px] font-code uppercase tracking-[0.18em] text-zinc-500 mb-2">
            {label}
        </label>
        <input
            type={type}
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
        />
    </div>
);
